// Modal — generic dialog: dimmed overlay, centered panel with an icon-tile
// header, scrollable body and an optional footer action row. Rendered in a
// portal; closes on Escape / overlay click; restores focus on close.

import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import IconButton from './IconButton';

export type ModalSize = 'sm' | 'md' | 'lg';
export type ModalTone = 'brand' | 'warning' | 'danger' | 'success';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Kebab-case Icon name shown in the tinted header tile. */
  icon?: string;
  /** Tint of the header icon tile. Default 'brand'. */
  tone?: ModalTone;
  size?: ModalSize;
  /** Action row below the body — rendered only when provided. */
  footer?: ReactNode;
  /** Close when the overlay (outside the panel) is clicked. Default true. */
  closeOnOverlay?: boolean;
  className?: string;
  children: ReactNode;
}

const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  tone = 'brand',
  size = 'md',
  footer,
  closeOnOverlay = true,
  className = '',
  children,
}: ModalProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape closes; focus moves into the panel on open and back on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const onOverlayMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlay && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className='app-modal' onMouseDown={onOverlayMouseDown}>
      <div
        ref={panelRef}
        className={['app-modal__panel', `app-modal__panel--${size}`, className]
          .filter(Boolean)
          .join(' ')}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className='app-modal__header'>
          {icon && (
            <span className={`app-modal__tile app-modal__tile--${tone}`}>
              <Icon name={icon} size={20} />
            </span>
          )}
          <span className='app-modal__heading'>
            <h2 id={titleId} className='app-modal__title'>
              {title}
            </h2>
            {subtitle && <span className='app-modal__subtitle'>{subtitle}</span>}
          </span>
          <IconButton
            icon='x'
            variant='ghost'
            className='app-modal__close'
            aria-label={t('modal_close')}
            onClick={onClose}
          />
        </header>

        <div className='app-modal__body'>{children}</div>

        {footer && <footer className='app-modal__footer'>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
